// AuthContext.js
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from "react";
import { getMyProfile } from "../api/ProfileApis";

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasCheckedAuth = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        setIsLoggedIn(false);
        setUser(null);
        return;
      }

      const response = await getMyProfile();

      if (response?.status === 200) {
        setIsLoggedIn(true);
        setUser(response.data);
      } else {
        setIsLoggedIn(false);
        setUser(null);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
      setIsLoggedIn(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasCheckedAuth.current) {
      hasCheckedAuth.current = true;
      checkAuth();
    }
  }, [checkAuth]);

  const login = useCallback((userData, tokens) => {
    setIsLoggedIn(true);
    setUser(userData);
    if (tokens) {
      const access = tokens.access ?? tokens.access_token;
      const refresh = tokens.refresh ?? tokens.refresh_token;
      if (access) localStorage.setItem('access_token', access);
      if (refresh) localStorage.setItem('refresh_token', refresh);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setIsLoggedIn(false);
    setUser(null);
  }, []);

  const value = { isLoggedIn, user, isLoading, login, logout, checkAuth };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};